import { SvgIconTypeMap } from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { useCallback, useEffect, useMemo, useState, useTransition } from 'react';
import { toast } from 'react-toastify';

import InventoryValue from '@/assets/fulfill/inventory/InventoryValue';
import InventoryVolume from '@/assets/fulfill/inventory/InventoryVolume';
import Scanner from '@/assets/fulfill/inventory/Scanner';
import SuccessfulDeliveries from '@/assets/fulfill/inventory/SuccessfulDeliveries';
import TotalAvailable from '@/assets/fulfill/inventory/TotalAvailable';
import TotalInProcess from '@/assets/fulfill/inventory/TotalInProcess';
import TotalPhysical from '@/assets/fulfill/inventory/TotalPhysical';
import { initializeHttpProvider } from '@/providers/HttpProvider/initializeHttpProvider';
import { OverallStats } from '@/types/fulfill/inventory';

interface FormattedStats {
  Icon: OverridableComponent<SvgIconTypeMap<object, 'svg'>>;
  title: string;
  amount: string | number;
}

interface UseStatsOptions {
  activeSkuCount: number | null;
}

const useStats = ({ activeSkuCount }: UseStatsOptions) => {
  const [rawStats, setRawStats] = useState<FormattedStats[]>([]);
  const [isPendingStats, startTransitionStats] = useTransition();

  const getStats = useCallback(async () => {
    const http = initializeHttpProvider();
    startTransitionStats(async () => {
      try {
        const response = await http.inventory.overallStats();
        setRawStats(formatData(response.data));
      } catch {
        setRawStats([]);
        toast.error('Error al obtener las estadísticas');
      }
    });
  }, [startTransitionStats]);

  useEffect(() => {
    void getStats();
  }, [getStats]);
  const stats = useMemo(() => {
    if (rawStats.length === 0) return rawStats;
    if (activeSkuCount === null) return rawStats;
    return rawStats.map((stat) =>
      stat.title === 'SKU Disponibles' ? { ...stat, amount: activeSkuCount } : stat,
    );
  }, [rawStats, activeSkuCount]);

  return {
    isPendingStats,
    stats,
  };
};

export default useStats;

const formatData = (data: OverallStats): FormattedStats[] => {
  return [
    {
      Icon: Scanner,
      title: 'SKU Disponibles',
      amount: data.totalProducts,
    },
    {
      Icon: InventoryValue,
      title: 'Valor del inv.',
      amount: `$${Number(data.totalInventoryValue).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
    },
    {
      Icon: InventoryVolume,
      title: 'Volumen del inv.',
      amount: `${Number(data.totalVolume).toFixed(2)}m³`,
    },
    {
      Icon: TotalPhysical,
      title: 'Total físico',
      amount: data.totalStock,
    },
    {
      Icon: TotalAvailable,
      title: 'Total disponible',
      amount: data.totalInventory,
    },
    {
      Icon: TotalInProcess,
      title: 'Total en proceso',
      amount: data.totalLeftover,
    },
    {
      Icon: SuccessfulDeliveries,
      title: 'Envíos realizados',
      amount: data.totalOrders,
    },
  ];
};
